import Commons from "oc-commons/Commons";
import {ValidationMetadata} from "./ValidationMetadata";

/**
 * definiert in einer filterPathIntersection, dass alle folgende Objekt / Map - Paare optional sind,
 * d.h. falls die intersection dadurch leer würde, werden diese Paare nicht angewendet
 * @type {symbol}
 */
export const following_are_optionals = Symbol('following_are_optionals');

/**
 * Erwartet als Eingabe jeweils abwechselnd ein Metadata-Objekt und eine Map-Funktion
 * und gibt alle passenden Metadata-Objekte zurück.
 * Die Map-Funktion erhält einen PathProxy des Metadata-Objekt, und muss einen
 * Path hin zur Ziel-Tabelle abbilden. Alle Paths müssen in derselben Zieltabelle enden.
 * filterPathIntersection gibt dann alle Intersections all dieser Paths zurück, wobei
 * das optionale Sortierung - Feld die Reihenfolge bestimmt.
 * Beispiel:
 * const elemente = filterPathIntersection(
 *   elemente, p=>p,
 *   this.serie, p=>elementOrTor(p.DetailSerie_Objekt).Füllung_Objekt.Füllung_Auswahlwert,
 *   this.massAuswahlwert, p=>elementOrTor(p.Mass_Auswahlwert.Mass).Füllung_Objekt.Füllung_Auswahlwert
 *   following_are_optionals,
 *   this.füllungAuswahlwert, p=>p.Füllung_Objekt
 * );
 * Tipp: Die Pfade lassen sich zur Laufzeit in der Console ermitteln
 * mit der Methode APP.richMetadata.shortestMetaPathAsSyntax("Start-Tabelle"","Ziel-Tabelle")
 * @param objectPathfunctionPairs
 * @returns {*}
 */
export function filterPathIntersection(...objectPathfunctionPairs) {
    const optionalIndex = objectPathfunctionPairs.findIndex(o=>o===following_are_optionals);
    if (optionalIndex>=0) {
        objectPathfunctionPairs.splice(optionalIndex,1);
    }
    const pairs = Commons.pairwise(objectPathfunctionPairs,2,2);
    const resultSets = pairs.map(([object,pathFunction])=>
        pathFunction(pathProxy(object)).exitProxySet());
    if (resultSets.length==0) {
        return [];
    }
    const intersection = resultSets.reduce((result,set, index)=>{
        const intersect = result.intersection(set);
        const optional = index*2>=optionalIndex;
        return optional && intersect.length==0
                ? result
                : intersect;
    });
    return sortForSortierung(intersection.toArray());
}

/**
 * wie filterPathIntersection, gibt aber nur den ersten Treffer zurück
 * @param objectPathfunctionPairs
 * @returns {unknown}
 */
export function findPathIntersection(...objectPathfunctionPairs) {
    return _.first(filterPathIntersection(...objectPathfunctionPairs));
}


/**
 * sortiert nach dem Standardfeld Sortierung
 * @param array
 * @returns {*}
 */
export function sortForSortierung(array) {
    return array.filter(x=>x!=null).sort((a, b) => a.Sortierung - b.Sortierung);
}

/**
 * gibt einen Proxy zurück, mit dem man wie in einer Breitensuche durch eine Objektstruktur navigieren kann.
 * Null oder Undefined Einträge werden rausgefiltert.
 * Mit .exitProxy() verlässt man den Proxy und erhält ein Array zurück, das mittels sortForSortierung sortiert ist.
 * Mit .exitProxySet() verlässt man den Proxy und erhält einen Set zurück.
 * Mit .proxyUnion(...mappers) übergibt man eine Reihe von Path-Funktionen, auf deren Ergebnisse union angewendet wird, beispiel: .proxyUnion(p=>p.Element,p=>p.Tor).
 * Mit .proxyIntersection(...mappers) analog zu .proxyUnion.
 *
 * Beispiel: pathProxy(mein).Auto.Räder.Radmuttern.exitProxy() liefert ein Array von allen Radmuttern des Autos und ist eine
 * Kurzschreibweise für _.uniq((mein.Auto||[]).flatMap(x=>x.Räder.flatMap(x=>x.Radmuttern)).filter(x=>x!=null))
 *
 * @param objectOrArray
 */
export function pathProxy(objectOrArray) {
    const toArray = (objectOrArray) => {
        if (objectOrArray==null) {
            return [];
        } else if (Array.isArray(objectOrArray)) {
            return objectOrArray;
        } else if (objectOrArray instanceof Set) {
            return Array.from(objectOrArray);
        }
        return [objectOrArray];
    }
    const proxy = (array) => new Proxy(array, {
        get(array, prop, receiver) {
            if (prop=='exitProxy') {
                return ()=>sortForSortierung(_.uniq(array));
            }
            if (prop=='exitProxySet') {
                return ()=>new Set(array.filter(x=>x!=null));
            }
            const reduceProxies = reducer => (...pathCallbacks) => {
                const sets = pathCallbacks.map(c=>c(receiver).exitProxySet());
                if (sets.length==0) {
                    return proxy([]);
                }
                return proxy(sets.reduce(reducer).toArray());
            }
            if (prop=='proxyUnion') {
                return reduceProxies((set,s)=>set.union(s));
            }
            if (prop=='proxyIntersection') {
                return reduceProxies((set,s)=>set.intersection(s));
            }
            return proxy(array.flatMap(x=>{
                if (x==null) {
                    return [];
                }
                if (x.adjacency && prop in x.adjacency) {
                    return toArray(x.adjacency[prop]);
                }
                return toArray(x[prop]);
            }));
        }
    });
    return proxy(toArray(objectOrArray));
}

/**
 * Wandelt die metadata in einen Graphen um (Adjazenzlisten)
 * und stellt eine API bereit
 */
export class GraphMetadata extends ValidationMetadata {

    /**
     * Gibt zu einer Row die Ursprungstabelle zurück (Liste aller Rows)
     * @type {Map<tableRow,Table>}
     */
    row2AdjazencedTable = new Map();

    /**
     * @type {{[tableName]: TableMeta}}
     */
    adjacencyMetaTableMap = {};

    constructor(metadata) {
        super(metadata);
    }

    async init() {
        await super.init();
        this._initAdjacencySets();
    }

    /**
     * Findet den kürzesten Weg zwischen zwei Tabellen, rein informell auf der Meta-Ebene
     */
    shortestMetaPathAsSyntax(startMetaName,goalMetaName) {
        const [startMeta,goalMeta] = [startMetaName,goalMetaName].map(name=>{
            const meta = this.adjacencyMetaTableMap[name];
            if (meta==null) {
                throw new Error('unknown adjacencyMetaTable '+name);
            }
            return meta;
        });
        return this._shortestPathWithNonEmptySet(startMeta,goalMeta).map(node=>node.name).join('.');
    }

    /**
     * findet zu einer from-row (fromMetaObject) die erste davon erreichbare Row des targetTable
     * @param fromRow
     * @param targetTableName
     * @param options siehe graphQuery
     * @returns die erste gefundene row des targetTable
     */
    findNearest(fromRow,targetTableName,options=undefined) {
        const rows = this.graphQuery(targetTableName,[fromRow], options);
        return _.first(rows);
    }

    /**
     * Bei einem gegebenen Set von vorselektierten Tabellen-Einträgen (context),
     * finde alle dadurch implizit festgelegten Einträge eines selectTables.
     *
     * Typisches Beispiel sind ausgewählte Auswahlwerte (context), die ein Element festlegen.
     *
     * Die Bedingung dafür ist, dass das Datenmodell graphartig aufgebaut ist,
     * also alle Beziehungen der Daten explizit über Foreign Keys definiert sind.
     *
     * Achtung: Es kann vorkommen, das Pfade gefunden werden, die so nicht gedacht waren.
     * Das passiert, weil der Graph bisher nicht gerichtet ist. Sollte das Problem auftreten,
     * sollte man die funktional gleichwertige, aber mit explizit festgelegten Pfaden arbeitende
     * Methode findPathIntersection verwenden.
     *
     * @param {string|string[]} selectTableNames ein Name oder ein Array. Falls es ein Array ist,
     * dann läuft die Suche über alle angegebenen Tabellen in dieser Reihenfolge.
     * @param {Set|Array} context
     * @param {{strict:boolean}} strict=false ignoriert Einschränkungen über den context, falls die
     * Ergebnismenge leer würde. Die Reihenfolge im context-Array bestimmt dabei die Priorität
     * (letzte werden zuerst ignoriert)
     * @param {{errorIfEmpty:boolean}} errorIfEmpty=true wirft einen Fehler, falls die Ergebnismenge leer ist
     */
    graphQuery(selectTableNames, context, {strict = true, errorIfEmpty = false, logDebugInfo = false} = {}) {
        if (selectTableNames==null) {
            throw new Error('no selectTableNames');
        }
        if (_.isString(selectTableNames)) {
            selectTableNames = [selectTableNames];
        }
        const selectTableName = _.last(selectTableNames);
        const selectMetaTables = selectTableNames.map(name=>this.adjacencyMetaTableMap[name]);
        const pathResults = [];
        const resultSet = (()=>{
            const notNullContext = context.filter(x => x != null);
            if (notNullContext.length == 0) {
                return this.metadata[selectTableName];
            }
            notNullContext.forEach(context => {
                if (!_.isPlainObject(context)) {
                    debugger;
                    console.error('not a plain object:', context);
                }
            });
            let resultSet = null;
            const mapEntry2Table = entry => {
                const table = this.row2AdjazencedTable.get(entry);
                if (table == null) {
                    debugger;
                    console.error('entry with unknown table:', entry);
                }
                return table;
            };
            const table2ContextRowsMaps = Commons.groupByFlat(notNullContext, (entry) => mapEntry2Table(entry));
            table2ContextRowsMaps.forEach((fixedRowsSet, fixedMetaTable) => {
                // 1. finde pro fixedTables einen Path per IDS zu dem selectTable
                const path = this._shortestPathWithHobsCached([fixedMetaTable].concat(selectMetaTables));
                if (path==null) {
                    return;
                }

                // 2. iteriere jeden einzelnen Path und bilde für jeden Path die Union-Sets der Werte
                const startProxy = pathProxy(fixedRowsSet);
                const endProxy = this._mapPathToPathProxyResultSet(path, startProxy);
                const pathResultSet = new Set(endProxy.exitProxy());
                pathResults.push({path,pathResultSet,fixedRowsSet});
                // 3. gib die Intersection aller Path-Sets zurück
                if (resultSet == null) {
                    resultSet = pathResultSet;
                } else {
                    const intersection = resultSet.intersection(pathResultSet);
                    if (intersection.length > 0 || strict) {
                        resultSet = intersection;
                    }
                }
            });
            return resultSet;
        })();
        const resultList = resultSet == null ? [] : Array.from(resultSet);
        if (logDebugInfo) {
            console.log('graphQuery',{logDebugInfo,selectMetaTables,context,strict,errorIfEmpty,pathResults});
        }
        if (errorIfEmpty && resultList.length==0) {
            const msg = 'no '+selectTableName+' reachable from context';
            console.error(msg,{selectMetaTables,context,strict,errorIfEmpty,pathResults});
            throw new Error(msg);
        }
        return resultList;
    }

    _initAdjacencySets() {

        // Aufsplitten der Tabellen / adjacencyMetaTableMap liefert Tabellen nach Typ getrennt.
        // Allerdings entsprechen die adjacency dieser Tabellen dann nicht mehr nach dieser Trennung, sondern wieder den ursprünglichen Tabellennamen
        // dazu müsste pro FK-Eintrag der Name der Ziel-Tabelle ermittelt werden und mit dem Property-Namen vermengt werden...
        // in manchen Situationen mag der Property-Name notwendig sein, wenn zwei auf die gleiche Tabelle gehen.
        // Taucht das auf? Sonst erst mal nur Typweise betrachten..

        const row2table = this.row2AdjazencedTable;

        Object.keys(this.metadata).forEach(runtimeTableName=>{
            const metaTable = this.metaTableMap[runtimeTableName];
            if (metaTable===undefined || metaTable.generateSubTableColumn===undefined) {
                const table = this.metadata[runtimeTableName];
                if (Array.isArray(table)) {
                    const adjacencyMetaTable = this.adjacencyMetaTableMap[runtimeTableName] = {name:runtimeTableName,table,adjacency:{}};
                    table.forEach(row=>{
                        row2table.set(row,adjacencyMetaTable);
                        row.adjacency = {};
                    });
                }
            }
        });


        // 1. Warum gibt es Serie und Serie_Objekt? Warum sind beide Tabellen nicht dasselbe?
        // 2. Was ist mit Objekt und DetailSerie_Objekt? Welche Tabelle hat da die Adjazenz? Immer die spezifischere..
        const maybeAddAdjacency = (adjacencyMetaTable,row,propertyName,value)=>{
            const vMetaTable = row2table.get(value);
            if (vMetaTable) {
                const refName = vMetaTable.name; // war: propertyName
                if (row.adjacency[refName]===undefined) {
                    row.adjacency[refName] = new Set();
                }
                row.adjacency[refName].add(value);

                if (adjacencyMetaTable.adjacency[refName]==undefined) {
                    adjacencyMetaTable.adjacency[refName] = new Set();
                }
                adjacencyMetaTable.adjacency[refName].add(vMetaTable);
            }
        }

        const blackList = new Set(['id','adjacency','updated_at','created_at']);

        row2table.forEach((adjacencyMetaTable,row)=>{
            Object.keys(row).forEach(propertyName=>{
                if (blackList.has(propertyName)) {
                    return;
                }
                const value = row[propertyName];
                Commons.forOneOrEach(value)(v=>maybeAddAdjacency(adjacencyMetaTable,row,propertyName,v));
            });
        });

    }

    /**
     * shortestPath als iterative deepening search über einen Adjacencygraph (darf auf Meta- oder
     * Rowebene sein) normaler bfs klappt nicht, weil die Gültigkeit einer Lösung pfadabhängig ist.
     * Achtung: Teure Operation
     */
    _shortestPath(startNode, isGoal) {
        const dls = (currentNode, currentDepth, maxDepth, path) => {
            if (isGoal(currentNode,path)) {
                return path;
            }
            if (currentDepth >= maxDepth) {
                return null;
            }
            for (let key of Object.keys(currentNode.adjacency)) {
                for (let childNode of currentNode.adjacency[key]) {
                    if (!path.includes(childNode)) {
                        const found = dls(childNode, currentDepth+1, maxDepth, path.concat([childNode]));
                        if (found) {
                            return found;
                        }
                    }
                }
            }
            return null;
        }
        for (let maxDepth = 1; maxDepth < 8; maxDepth++) {
            const foundPath = dls(startNode, 0, maxDepth, [startNode]);
            if (foundPath) {
                return foundPath;
            }
        }
        return [];
    }

    _shortestPathWithNonEmptySet(startNode,endNode) {
        const startProxy = pathProxy(startNode.table);
        return this._shortestPath(startNode,(node,path)=>{
            if (node!==endNode) {
                return false;
            }
            const endProxy = this._mapPathToPathProxyResultSet(path,startProxy);
            const array = endProxy.exitProxy();
            return array.length!=0;
        });
    }

    _shortestPathWithNonEmptySetCached = (()=>{
        const cache = [];
        return (startNode,endNode) => {
            const cacheEntry = cache.find((cacheEntry)=>cacheEntry[0]===startNode && cacheEntry[1]===endNode);
            if (cacheEntry) {
                return cacheEntry[2];
            }
            const result = this._shortestPathWithNonEmptySet(startNode,endNode);
            cache.push([startNode,endNode,result]);
            return result;
        }
    })();

    _mapPathToPathProxyResultSet(path,rowsSetProxy) {
        path.slice(1).forEach(node=> rowsSetProxy = rowsSetProxy[node.name]);
        return rowsSetProxy;
    }

    _shortestPathWithHobsCached(hobs) {
        const pairs = Commons.pairwise(hobs);
        const paths = pairs.map(([a,b],index)=>{
            const path = this._shortestPathWithNonEmptySetCached(a, b);
            if (path.length==0) {
                return null;
            }
            return index==0 ? path : path.slice(1);
        });
        if (paths.some(p=>p==null)) {
            return null;
        }
        return paths.flatMap(a=>a);
    }

}

window.GraphMetadata = GraphMetadata;
